<template>
  <nav aria-label="">
    <ul class="pagination justify-content-end">
      <li class="page-item" :class="atFirstPage || totalPages == 0 ? 'disabled' : ''">
        <a class="page-link" tabindex="-1" aria-disabled="true" data-test="anchorPaginationPrevious" @click="onClickPreviousPage">
          <IconArrowRight/>
        </a>
      </li>

      <li class="disabled">
        <input
          class="form-pagination"
          type="text"
          name=""
          v-model.number="internalCurrentPage"
          @blur="onClickPage(internalCurrentPage)"
          @keypress.enter="onClickPage(internalCurrentPage)"
          @input="filterNonNumbers">
        <span> de {{ totalPages }}</span>
      </li>
      <li class="page-item" :class="atLastPage ||  totalPages == 0 || total < 20  ? 'disabled' : ''">
        <a class="page-link" tabindex="-1" aria-disabled="true" data-test="anchorPaginationNext" @click="onClickNextPage">
          <IconArrowRight/>
        </a>
      </li>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
  .pagination {
    display:                           flex;
    align-items:                       center;
    gap:                               5px;
    margin:                            0;

    li:not(.page-item) {
      line-height:                     0;

      span {
        color:                         var(--Shades-80);
        font-size:                     var(--font-size-14);
        font-weight:                   400;
        line-height:                   normal;
        text-overflow:                 ellipsis;
        overflow:                      hidden;

        &:first-child {
          display: none;
        }
      }
    }

    .page-item {
      &:first-child {
        svg {
          transform:                   scaleX(-1);
        }
      }

      .page-link {
        @include                       border_radius(50%);
        display:                       flex;
        align-items:                   center;
        justify-content:               center;
        width:                         30px;
        height:                        30px;
        padding:                       8px 10px;
        border:                        1.5px solid var(--Shades-30);
        background:                    var(--Shades-W);
        cursor:                        pointer;
      }
      &.disabled {
        &,
        &:focus,
        &:hover {
          color:                       $taupe-gray;
          pointer-events:              unset;
          cursor:                      not-allowed;
          opacity:                     0.4;
        }
      }
    }

    .form-pagination {
      @include                         border_radius(1px);
      color:                           var(--Shades-80, #232323);
      font-size:                       var(--font-size-14);
      font-weight:                     500;
      text-overflow:                   ellipsis;
      text-align:                      center;
      line-height:                     normal;
      width:                           34px;
      height:                          24px;
      padding:                         0px 5px;
      overflow:                        hidden;
      border:                          0;
      border-bottom:                   1px solid var(--Shades-40);
      background:                      var(--Shades-10);
    }
  }

  .has-filter {
    .pagination {
      .disabled:not(.page-item) {
        span:first-child {
          display: block;
        }
        input, span:last-child {
          display: none;
        }
      }
    }
  }

  @media (max-width: 767px) {
    .pagination {
      li:not(.page-item) {
        span:last-child {
          display:                     none;
        }
      }
    }
  }
</style>

<script>
  export default {
    name: 'BasePagination',
    props: {
      maxVisibleButtons: {type: Number, required: false, default: 5},
      totalPages: {type: Number, required: true},
      total: {type: Number, required: true},
      perPage: {type: Number, required: true},
      currentPage: {type: Number, required: true},
    },
    data: () => ({
      internalCurrentPage: 1
    }),
    watch: {
      currentPage: {
        immediate: true,
        handler: function(newValue) {
          if (newValue !== undefined && newValue !== null)
            this.internalCurrentPage = Number(newValue)
      }},
    },
    computed: {
      startPage() {
        const current = Number(this.currentPage);

        if (current === 1)
          return 1;

        if (current === this.totalPages)
          return this.totalPages - this.maxVisibleButtons + 1;

        return current - 1;
      },
      endPage() {
        return Math.min(Number(this.startPage) + this.maxVisibleButtons - 1, this.totalPages);
      },
      pages() {
        const range = [];

        for (let i = Number(this.startPage); i <= Number(this.endPage); i += 1 ) {
          if (i > 0)
            range.push({name: i, isDisabled: i === this.currentPage});
        }

        return range;
      },
      atFirstPage() {
        return Number(this.currentPage) === 1;
      },
      atLastPage() {
        return Number(this.currentPage) === this.totalPages;
      },
    },
    mounted() {
      if (this.currentPage !== undefined && this.currentPage !== null)
        this.internalCurrentPage = Number(this.currentPage);

      window.addEventListener('keydown', this.handleGlobalPaginationKeys)
    },
    beforeUnmount() {
      document.removeEventListener('keydown', this.handleGlobalPaginationKeys);
    },
    methods: {
      onClickFirstPage() {
        this.$emit('pagechanged', 1);
      },
      onClickPreviousPage() {
        this.$emit('pagechanged', Number(this.currentPage) - 1);
      },
      onClickPage(page) {
        const pageNumber = Number(page);

        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > this.totalPages || pageNumber === Number(this.currentPage)) {
          this.internalCurrentPage = Number(this.currentPage);
          return;
        }

        this.$emit('pagechanged', pageNumber);
      },
      onClickNextPage() {
        this.$emit('pagechanged', Number(this.currentPage) + 1);
      },
      onClickLastPage() {
        this.$emit('pagechanged', this.totalPages);
      },
      isPageActive(page) {
        return Number(this.currentPage) === page;
      },
      handleGlobalPaginationKeys(event){
        if (this.isAnyOverlayOpen())
          return

        if (event.key === 'ArrowLeft') {
          this.onClickPage(this.currentPage - 1)
        } else if (event.key === 'ArrowRight') {
          if (this.currentPage < this.totalPages)
            this.onClickPage(this.currentPage + 1)
        }
      },
      isAnyOverlayOpen() {
        const isLightGalleryOpen = document.querySelector('body .lg-container.lg-show.lg-show-in');
        const homeHeaderSearchBox = document.querySelector('header .search-box');
        const menuMobileSearchBox = document.querySelector('.menu-mobile-box .search-box');
        const isOffcanvasOpen = document.querySelector('.offcanvas-backdrop.fade.show');

        let isSearchBarOrFilterOpen = false;

        if (homeHeaderSearchBox && (homeHeaderSearchBox.classList.contains('open') || homeHeaderSearchBox.classList.contains('expand')))
          isSearchBarOrFilterOpen = true;

        if (menuMobileSearchBox && (menuMobileSearchBox.classList.contains('open') || menuMobileSearchBox.classList.contains('expand')))
          isSearchBarOrFilterOpen = true;

        return !!isLightGalleryOpen || isSearchBarOrFilterOpen || isOffcanvasOpen
      },
      filterNonNumbers() {
        let value = String(this.internalCurrentPage);
        value = value.replace(/[^0-9]/g, '');
        this.internalCurrentPage = value;
      },
    }
  }
</script>
